import cn from 'classnames';
import Link from 'next/link';
// Types
import {
  FeaturedStreamsType,
  getFeaturedStreamActionState,
  getFeaturedSlideDetails,
} from '../FeaturedStreamsCarousel.helpers';
// UI
import Text from 'ui3/Text/Text';
// Components
import Button from 'ui3/Button/Button';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import TalentBlock from 'components/common3/TalentBlock/TalentBlock';
import GetTicketsButton from 'components/TicketEvents/GetTicketsButton/GetTicketsButton';
// Hooks
import { useGetCurrUser, useStreamPurchaseNavigation } from 'hooks';
import { useTicketingLaunched } from 'hooks/useTicketingLaunched';
// Helpers
import { parseToPlainText } from 'helpers/textEditor';
import { getStreamerAvatar, getStreamerName } from 'helpers/streams';
// Styles
import styles from './MobileCarousel.module.scss';

interface MobileCarouselProps {
  streams: FeaturedStreamsType[];
  currentSlide: number;
  onSlideChange: (slideIndex: number) => void;
  useArrowNavigation?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  showOfficialPpvTag?: boolean;
  withOrganizer?: boolean;
  /** Pauses autoplay while a checkout opened from a slide is open. */
  onCheckoutOpenChange?: (open: boolean) => void;
}

const MobileCarousel = ({
  streams,
  currentSlide,
  onSlideChange,
  useArrowNavigation = false,
  onPrev,
  onNext,
  showOfficialPpvTag = true,
  withOrganizer = false,
  onCheckoutOpenChange,
}: MobileCarouselProps) => {
  const { data: userData } = useGetCurrUser();
  const { navigateToStream } = useStreamPurchaseNavigation();
  const ticketingLaunched = useTicketingLaunched();
  const currentStream = streams?.[currentSlide];

  if (!currentStream || !currentStream.stream) {
    return null;
  }

  const stream = currentStream.stream;
  const {
    buttonText,
    hasAccessToWatch,
    isUserStreamCreator,
    showOfficialPpvTagForSlide,
  } = getFeaturedStreamActionState(
    stream,
    userData?.me?.id,
    showOfficialPpvTag
  );

  const {
    streamPath,
    dateLabel,
    organizer,
    ticketEvent,
  } = getFeaturedSlideDetails(stream, withOrganizer);

  const handleBuyButtonClick = () => {
    navigateToStream(currentStream.stream, hasAccessToWatch);
  };

  // Parse description for ellipsis
  const descriptionPlainText = parseToPlainText(
    currentStream.stream.description
  );

  const showIndicators = streams.length > 1;

  return (
    <div className={styles.mobileCarousel}>
      {/* Image Section with Tags Overlay */}
      <div className={styles.imageSection}>
        <div className={styles.imageWrapper}>
          {currentStream.stream.imageURL && (
            <img
              src={currentStream.stream.imageURL}
              alt={currentStream.stream.name || 'Event poster'}
              className={styles.posterImage}
            />
          )}
          <div className={styles.imageShading} />
          <div className={styles.mobileImageOverlap}>
            <div className={styles.tagsRow}>
              {showOfficialPpvTagForSlide && (
                <div className={styles.officialPpvTag}>
                  <Text variant="smallLabelBold" color="background-dim">
                    Official PPV
                  </Text>
                </div>
              )}
              <div className={styles.dateTag}>
                <Text variant="smallLabelBold" color="background-dim">
                  {dateLabel}
                </Text>
              </div>
            </div>
            <Link href={streamPath} prefetch={false}>
              <a className={styles.eventTitleLink}>
                <h2 className={styles.eventTitle}>
                  {currentStream.stream.name}
                </h2>
              </a>
            </Link>
          </div>
        </div>

        {/* Arrow Navigation on Sides */}
        {useArrowNavigation && streams.length > 1 && (
          <>
            <button
              type="button"
              className={styles.arrowButtonLeft}
              onClick={onPrev}
              aria-label="Previous"
            >
              <MillionsIcon name="arrowLeft" className={styles.arrowIcon} />
            </button>
            <button
              type="button"
              className={styles.arrowButtonRight}
              onClick={onNext}
              aria-label="Next"
            >
              <MillionsIcon name="arrowRight" className={styles.arrowIcon} />
            </button>
          </>
        )}
      </div>

      {/* Content Section Below Image */}
      <div className={styles.contentSection}>
        {organizer && (
          <TalentBlock
            store={organizer}
            talentName={getStreamerName(organizer)}
            avatarURL={getStreamerAvatar(organizer.storeDetails)}
            profilePath={`/${organizer.slug}`}
            showFollowButton={!isUserStreamCreator}
            className={styles.organizer}
          />
        )}

        {descriptionPlainText && (
          <div
            className={cn(styles.description, {
              [styles.descriptionWithReadMore]: withOrganizer,
            })}
          >
            {descriptionPlainText}
          </div>
        )}

        {withOrganizer && descriptionPlainText && (
          <Link href={streamPath} prefetch={false}>
            <a className={styles.readMore}>Read More</a>
          </Link>
        )}

        <Button
          variant="primary"
          size="large"
          className={styles.buyButton}
          onClick={handleBuyButtonClick}
        >
          {buttonText}
        </Button>

        {ticketingLaunched && ticketEvent && (
          <GetTicketsButton
            key={ticketEvent.id}
            event={ticketEvent}
            size="large"
            className={styles.getTicketsButton}
            onOpenChange={onCheckoutOpenChange}
          />
        )}

        {/* Carousel Indicators */}
        {showIndicators && (
          <div className={styles.indicators}>
            {streams.map((_, index) => (
              <button
                type="button"
                key={index}
                className={cn(styles.indicator, {
                  [styles.active]: index === currentSlide,
                })}
                onClick={() => onSlideChange(index)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileCarousel;
